import React from "react";
import { 
  Button, Card, CardBody, CardFooter, Link, 
  Modal, ModalContent, ModalBody, useDisclosure, Image 
} from "@heroui/react";
import { motion } from "framer-motion";
import { Icon } from "@iconify/react";
import { useHistory } from "react-router-dom";
import { Helmet } from 'react-helmet-async';
import { BASE_URL } from "../services/config";
import { NewsSection } from "../components/news-section";
import AboutUsBanner from "../assets/images/about-banner.png";
import AboutPhoto from "../assets/images/about-main-2.jpg";
import AboutPhoto2 from "../assets/images/about-main-3.jpg";
import AboutMain from "../assets/images/about-main.jpg";
import productCandles from "../assets/images/products/candle-main.png";
import productDiffuse from "../assets/images/products/diffuse-main.png";
import productSashe from "../assets/images/products/sashe-main.png";
import productMelts from "../assets/images/products/melts-main.png";
import productAuto from "../assets/images/products/auto-main.png";
import productMasage from "../assets/images/products/massage-main.png";
import productLanch from "../assets/images/products/lanch-candle-main.png";
import productDecorate from "../assets/images/products/decorate-main.png";

interface AboutUsPageProps {
  onProductTypeSelect: (type: string) => void;
}

interface NewsItem {
  id: number;
  title: string;
  excerpt: string;
  date: string;
  image: string;
  category: string;
}

interface Material {
  icon: string;
  title: string;
  description: string;
}

interface Certificate {
  id: number;
  title: string;
  image: string;
  institution?: string;
  date?: string;
  icon?: string;
}

export const AboutUsPage: React.FC<AboutUsPageProps> = ({ onProductTypeSelect }) => {
  const history = useHistory();
  const newsItems: NewsItem[] = [
    {
      id: 1,
      title: "Новая коллекция ароматов «Северное сияние»",
      excerpt: "Представляем новую лимитированную коллекцию, вдохновленную красотой северной природы.",
      date: "15 мая 2023",
      image: "https://img.heroui.chat/image/landscape?w=600&h=400&u=northern1",
      category: "Новинки"
    },
    {
      id: 2,
      title: "Мастер-класс по созданию свечей",
      excerpt: "Приглашаем вас на мастер-класс, где вы сможете создать свою уникальную ароматическую свечу.",
      date: "3 июня 2023",
      image: "https://img.heroui.chat/image/furniture?w=600&h=400&u=workshop1",
      category: "События"
    },
    {
      id: 3,
      title: "Сотрудничество с сетью отелей Premium",
      excerpt: "Мы рады объявить о начале сотрудничества с международной сетью отелей Premium.",
      date: "22 июня 2023",
      image: "https://img.heroui.chat/image/furniture?w=600&h=400&u=hotel1",
      category: "Партнерства"
    }
  ];

  const materials: Material[] = [
    {
      icon: "lucide:droplet",
      title: "Кокосовый воск",
      description: "Натуральный, экологичный воск, получаемый из кокосового масла. Обеспечивает чистое горение без копоти и токсинов."
    },
    {
      icon: "lucide:flask-conical",
      title: "Парфюмерные масла",
      description: "Высококачественные масла без фталатов и других вредных химических соединений, создающие стойкий и насыщенный аромат."
    },
    {
      icon: "lucide:wine",
      title: "Переработанное стекло",
      description: "Наши контейнеры изготовлены из переработанного стекла, что снижает воздействие на окружающую среду и придает изделиям уникальный вид."
    },
    {
      icon: "lucide:leaf",
      title: "Хлопковые фитили",
      description: "Натуральные хлопковые фитили без свинца обеспечивают равномерное горение и чистый аромат без посторонних запахов."
    },
    {
      icon: "lucide:package",
      title: "Экологичная упаковка",
      description: "Минималистичная упаковка из переработанных и биоразлагаемых материалов, которую можно использовать повторно."
    },
    {
      icon: "lucide:recycle",
      title: "Биоразлагаемые компоненты",
      description: "Все компоненты наших продуктов биоразлагаемы и не наносят вреда окружающей среде."
    }
  ];

  const productCategories = [
    {
      type: "candles",
      title: "Ароматические свечи",
      description: "Натуральный воск и премиальные ароматы",
      image: `${productCandles}`
    },
    {
      type: "massage_candles",
      title: "Массажные свечи",
      description: "Компактные ароматизаторы для небольших пространств",
      image: `${productMasage}`
    },
    // {
    //   type: "table_candles",
    //   title: "Столовые свечи",
    //   description: "Компактные ароматизаторы для небольших пространств",
    //   image: `${productLanch}`
    // },
    {
      type: "decorative_candles",
      title: "Декоративные свечи",
      description: "Компактные ароматизаторы для небольших пространств",
      image: `${productDecorate}`
    },
    {
      type: "auto_perfumes",
      title: "Автопарфюмы",
      description: "Компактные ароматизаторы для небольших пространств",
      image: `${productAuto}`
    },
    {
      type: "diffusers",
      title: "Диффузоры",
      description: "Стойкий аромат без использования огня",
      image: `${productDiffuse}`
    },
    {
      type: "sachets",
      title: "Саше",
      description: "Компактные ароматизаторы для небольших пространств",
      image: `${productSashe}`
    },
    // {
    //   type: "melts",
    //   title: "Мелтсы",
    //   description: "Восковые плитки для аромаламп",
    //   image: `${productMelts}`
    // }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };

  // Certificate modal state
  const {isOpen, onOpen, onClose, onOpenChange} = useDisclosure();
  const [selectedCertificate, setSelectedCertificate] = React.useState<Certificate | null>(null);
  
  // Certificates data
  const certificates: Certificate[] = [
    {
      id: 1,
      title: "Международный сертификат парфюмера",
      image: "https://img.heroui.chat/image/book?w=800&h=600&u=certificate1",
      institution: "International Perfume Academy",
      date: "2022",
      icon: "lucide:award"
    },
    {
      id: 2,
      title: "Мастер-класс по натуральным компонентам",
      image: "https://img.heroui.chat/image/book?w=800&h=600&u=certificate2",
      institution: "Grasse Institute of Perfumery",
      date: "2021",
      icon: "lucide:medal"
    },
    {
      id: 3,
      title: "Экологический сертификат продукции",
      image: "https://img.heroui.chat/image/book?w=800&h=600&u=certificate3",
      institution: "Eco-Cert Foundation",
      date: "2023",
      icon: "lucide:leaf"
    },
    {
      id: 4,
      title: "Диплом за инновации в производстве",
      image: "https://img.heroui.chat/image/book?w=800&h=600&u=certificate4",
      institution: "Beauty Industry Awards",
      date: "2022",
      icon: "lucide:sparkles"
    },
    {
      id: 5,
      title: "Сертификат качества ингредиентов",
      image: "https://img.heroui.chat/image/book?w=800&h=600&u=certificate5",
      institution: "Quality Standards Association",
      date: "2023",
      icon: "lucide:check-circle"
    },
    {
      id: 6,
      title: "Курс по аромапсихологии",
      image: "https://img.heroui.chat/image/book?w=800&h=600&u=certificate6",
      institution: "Международный институт ароматерапии",
      date: "2021",
      icon: "lucide:brain"
    }
  ];
  
  const handleOpenCertificate = (certificate: Certificate) => {
    setSelectedCertificate(certificate);
    onOpen();
  };

  return (
    <>
      <Helmet>
        <title>О бренде — SAFONOVA</title>
        <meta name="description" content="Мы создаем премиальные ароматические продукты ручной работы: свечи, диффузоры и саше. Узнайте нашу историю и миссию." />
        <link rel="canonical" href={`${BASE_URL || ''}/about`} />
        <meta property="og:title" content="О бренде — SAFONOVA" />
        <meta property="og:description" content="Мы создаем премиальные ароматические продукты ручной работы: свечи, диффузоры и саше. Узнайте нашу историю и миссию." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`${BASE_URL || ''}/about`} />
        <meta property="og:image" content={`${BASE_URL || ''}/og-default.png`} />
      </Helmet>
      {/* Hero Banner */}
      <section 
        className="relative flex items-center py-32 md:py-48"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.25), rgba(0, 0, 0, 0.10)), url('${AboutUsBanner}')`,
          backgroundPosition: "center 60%",
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat"
        }}
      >
        <div className="container mx-auto px-6 md:px-12">
          <motion.div 
            className="max-w-3xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-light mb-6 text-white text-shadow">
              О нашем <span className="font-semibold">бренде</span>
            </h1>
            <p className="text-lg md:text-xl text-white/90 mb-8 max-w-xl text-shadow">
              Мы создаем премиальные ароматические продукты ручной работы, которые наполняют пространство уютом и гармонией
            </p>
          </motion.div>
        </div>
      </section>

      {/* Intro / Founder story section (image left, quote right) */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <motion.div
              className="relative w-full max-h-[90vh] md:max-h-[720px] overflow-hidden"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              viewport={{ once: true, margin: "-100px" }}
            >
                <img
                  src={AboutMain}
                  alt="Мастерская SAFONOVA"
                  className="w-full h-full object-contain"
                  style={{ objectPosition: 'center 90%', transform: 'translateY(-14%)' }}
                />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              viewport={{ once: true, margin: "-100px" }}
            >
              <blockquote className="text-foreground/80 text-lg md:text-xl leading-relaxed italic">
                <p className="mb-4">Добро пожаловать в мир Safonova — мир уюта, гармонии и уникальных ароматов.</p>
                <p className="mb-4">Меня зовут Анна Сафонова, и моя история началась с простой, но важной страсти — стремления создать в доме атмосферу, где каждый уголок наполнен комфортом и неповторимыми ароматами.</p>
                <p className="mb-4">Как и многие из вас, я всегда искала свечи и ароматы, которые действительно наполняли бы дом запахом, но раз за разом сталкивалась с разочарованием. Мои ожидания не оправдывались, и я решила изменить это. Начав с глубокого изучения материалов, посещения курсов и экспериментов с рецептами, я нашла идеальный баланс для создания продукции, которая действительно работает.</p>
                <p className="mb-4">Так родился бренд Safonova — результат моей любви к уюту и желанию дарить его каждому. Сегодня мы с гордостью предлагаем ароматические свечи, диффузоры и аромасаше, которые действительно наполняют пространство приятными запахами, пробуждая воспоминания и создавая атмосферу тепла.</p>
                <p className="mb-4">За два года на рынке мы получили множество положительных отзывов, и для меня это самая большая награда. Я горжусь тем, что наша продукция дарит людям то, что искала сама — настоящие ароматы, которые делают дом уютным и особенным.</p>
              </blockquote>
              <div className="mt-6">
                <p className="text-sm font-medium">— Анна Сафонова, Основатель ароматического бренда SAFONOVA</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      <section className="py-24 bg-background">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              viewport={{ once: true, margin: "-100px" }}
            >
              <h2 className="text-3xl md:text-4xl font-light mb-6">Наша <span className="font-medium">миссия</span></h2>
              <p className="text-foreground/80 mb-6">
                Наша миссия — помочь вам создать атмосферу, в которой будет приятно находиться, где каждый день наполнен гармонией и комфортом. Зажечь в ваших сердцах огонь страсти, любви и ярких хороших воспоминаний через высококачественные ароматические свечи, диффузоры и аромасаше.
              </p>
              <p className="text-foreground/80 mb-6">
                В Safonova мы верим, что аромат — это ключ к созданию уюта и гармонии в каждом доме. Мы стремимся использовать только натуральные и экологически чистые ингредиенты, чтобы каждая свеча приносила не только удовольствие от аромата, но и безопасность для вашего здоровья.
              </p>
              <p className="text-foreground/80 mb-6">
                Мы вдохновляемся эмоциями и воспоминаниями, которые вызывает аромат, и стремимся создать уникальные композиции, способные пробудить самые тёплые чувства. Safonova — это не просто бренд, это ваш личный уют, запечатлённый в каждом аромате.
              </p>
            </motion.div>

            <motion.div 
              className="relative"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              viewport={{ once: true, margin: "-100px" }}
            >
              <div className="relative h-[500px] overflow-hidden">
                <img 
                  src={AboutPhoto} 
                  alt="О нашем бренде" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-primary p-6 max-w-xs">
                <p className="text-sm italic text-primary-foreground">
                  "Мы создаем не просто продукты, а эмоции и воспоминания, которые остаются с вами надолго"
                </p>
              </div>
            </motion.div>

            <motion.div 
              className="relative"
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              viewport={{ once: true, margin: "-100px" }}
            >
              <div className="relative h-[500px] overflow-hidden">
                <img 
                  src={AboutPhoto2} 
                  alt="О нашем бренде" 
                  className="w-full h-full object-cover"
                />
              </div>
              {/* <div className="absolute -bottom-6 -left-6 bg-primary p-6 max-w-xs">
                <p className="text-sm italic text-primary-foreground">
                  "Мы создаем не просто продукты, а эмоции и воспоминания, которые остаются с вами надолго"
                </p>
              </div> */}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              viewport={{ once: true, margin: "-100px" }}
            >                        
              <h2 className="text-3xl md:text-4xl font-light mb-6 mt-12">Наши <span className="font-medium">ценности</span></h2>
              <p className="text-foreground/80 mb-6">
                Бренд Safonova основан на глубоком уважении к качеству и натуральности. Я верю в силу природы и ее способность создавать волшебство, которое отражается в каждом моем аромате. Используя только высококачественные ингредиенты, я стремлюсь дарить вам лучшие ароматы, которые наполняют пространство уютом и гармонией.
              </p>
              <p className="text-foreground/80 mb-6">
                Важной ценностью для меня является индивидуальный подход к каждому клиенту. Я понимаю, что каждый ищет не просто аромат, а атмосферу и воспоминания, которые они несут. Поэтому я внимательно учитываю ваши пожелания и стремлюсь сделать каждую свечу, диффузор или аромасаше отражением вашего уникального стиля и характера.
              </p>
              <p className="text-foreground/80">
                Моя цель — создавать ароматы, которые будут не просто предметами, а частью вашей жизни, даря тепло и уют. Я уверена, что каждый мой продукт способен напомнить о лучших моментах, объединяя людей и создавая особую атмосферу в каждом доме.
              </p>
            </motion.div>
            
          </div>
        </div>
      </section>

      {/* Materials Section */}
      <section className="py-24 bg-content2">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-light mb-4">Наши <span className="font-medium">материалы</span></h2>
            <p className="text-foreground/70 max-w-2xl mx-auto">
              Мы используем только премиальные, экологичные материалы для создания наших продуктов
            </p>
          </div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            {materials.map((material, index) => (
              <motion.div key={index} variants={itemVariants}>
                <Card className="border border-divider bg-background h-full" disableRipple>
                  <CardBody className="p-6">
                    <div className="bg-primary/10 p-3 rounded-full w-12 h-12 flex items-center justify-center mb-4">
                      <Icon icon={material.icon} className="text-primary" width={24} />
                    </div>
                    <h3 className="text-lg font-medium mb-2">{material.title}</h3>
                    <p className="text-foreground/70 text-sm">{material.description}</p>
                  </CardBody>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* New Certificates Section */}
      {/* <section className="py-24 bg-content2">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-light mb-4">Сертификаты и <span className="font-medium">дипломы</span></h2>
            <p className="text-foreground/70 max-w-2xl mx-auto">
              Наша команда постоянно развивается и подтверждает свой профессионализм. Здесь вы можете увидеть часть наших достижений.
            </p>
          </div>

          <motion.div 
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            {certificates.map((certificate) => (
              <motion.div key={certificate.id} variants={itemVariants}>
                <Card 
                  className="border border-divider bg-content1 h-full product-card-hover" 
                  isPressable
                  onPress={() => handleOpenCertificate(certificate)}
                  disableRipple
                >
                  <CardBody className="p-0 overflow-hidden">
                    <div className="relative h-56 overflow-hidden">
                      <img 
                        src={certificate.image} 
                        alt={certificate.title} 
                        className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                      />
                      {certificate.icon && (
                        <div className="absolute top-4 right-4 bg-primary p-2 rounded-full">
                          <Icon icon={certificate.icon} className="text-primary-foreground" width={20} />
                        </div>
                      )}
                    </div>
                  </CardBody>
                  <CardFooter className="flex flex-col items-start p-6">
                    <h3 className="text-lg font-medium mb-2">{certificate.title}</h3>
                    <div className="flex items-center gap-2 text-foreground/70 text-sm">
                      {certificate.institution && (
                        <>
                          <Icon icon="lucide:building" width={14} />
                          <span>{certificate.institution}</span>
                        </>
                      )}
                    </div>
                    {certificate.date && (
                      <div className="flex items-center gap-2 text-foreground/70 text-sm mt-1">
                        <Icon icon="lucide:calendar" width={14} />
                        <span>{certificate.date}</span>
                      </div>
                    )}
                  </CardFooter>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section> */}

      {/* Certificate Modal */}
      {/* <Modal isOpen={isOpen} onOpenChange={onOpenChange} size="3xl">
        <ModalContent>
          {(onClose) => (
            <>
              <ModalBody className="p-0 relative">
                <Button
                  isIconOnly
                  variant="light"
                  className="absolute top-2 right-2 z-10 bg-foreground/10"
                  onPress={onClose}
                >
                  <Icon icon="lucide:x" width={20} />
                </Button>
                {selectedCertificate && (
                  <Image
                    alt={selectedCertificate.title}
                    src={selectedCertificate.image}
                    className="w-full h-auto max-h-[80vh]"
                  />
                )}
              </ModalBody>
            </>
          )}
        </ModalContent>
      </Modal> */}

      {/* Product Categories Section - Fixed grid responsiveness */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-light mb-4">Наша <span className="font-medium">продукция</span></h2>
            <p className="text-foreground/70 max-w-2xl mx-auto">
              Познакомьтесь с нашими коллекциями ароматической продукции ручной работы
            </p>
          </div>

          <motion.div 
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            {productCategories.map((category, index) => (
              <motion.div key={index} variants={itemVariants}>
                <Card 
                  className="border border-divider bg-content2/50 w-full h-full product-card-hover text-center"
                  isPressable
                  onPress={() => onProductTypeSelect(category.type)}
                  disableRipple
                >
                  <CardBody className="p-0 overflow-hidden">
                    <div className="relative h-64 overflow-hidden">
                      <img 
                        src={category.image} 
                        alt={category.title} 
                        className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
                      />
                    </div>
                  </CardBody>
                  <CardFooter className="flex flex-col p-6 text-center">
                    <h3 className="text-xl font-medium mb-2">{category.title}</h3>
                    <p className="text-foreground/70 text-sm mb-4">{category.description}</p>
                    {/* <Button 
                      color="primary" 
                      variant="flat" 
                      endContent={<Icon icon="lucide:arrow-right" width={16} />}
                      className="mt-auto"
                      onPress={() => onProductTypeSelect(category.type)}
                    >
                      Подробнее
                    </Button> */}
                  </CardFooter>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* News Section */}
      <section className="py-24 bg-content2">
        <div className="container mx-auto px-6">
          <NewsSection 
            onViewAllClick={() => history.push("/news-list")}
            onNewsItemClick={(newsId) => history.push(`/news-detail/${newsId}`)}
          />
        </div>
      </section>

      {/* Newsletter Section
      <NewsletterSection /> */}
    </>
  );
};